import type { PlayerUser } from '../../entities/user/types';
import { ApiError, apiFetch, authHeader, jsonBody } from '../../shared/api/httpClient';
import { clientHeaders } from '../../shared/lib/device';

export type RoomDistributionBet = {
  id?: number | string;
  betId?: number | string;
  user?: {
    id?: number | string;
    user_id?: number | string;
    userId?: number | string;
    player_id?: number | string;
    playerId?: number | string;
    username?: string;
    name?: string;
    initial?: string;
    vipLevel?: number;
    level?: number;
    flag?: string;
    // Already present in the distribution payload; the bet feed renders these
    // so a row shows the same face the leaderboard does.
    avatarDefaultId?: number;
    avatar_default_id?: number;
    avatarThumbUrl?: string;
    avatar_url?: string;
    // Heat-discovery signals — authoritative values computed by the backend
    // (streak across rounds, short-window win rate). Absent until that engine
    // ships; the 🔥 hot lane stays dormant while they are undefined.
    hot?: boolean;
    streak?: number;
    winStreak?: number;
    win_streak?: number;
    windowWinRate?: number;
    window_win_rate?: number;
    // 🧊 cold mirror — loss streak / short-window loss rate (fade candidates).
    cold?: boolean;
    lossStreak?: number;
    loss_streak?: number;
    windowLossRate?: number;
    window_loss_rate?: number;
  };
  game?: string;
  tab?: string;
  pick?: string;
  rawPick?: string | number;
  amount?: number;
  currency?: string;
  ts?: number;
  placedAt?: number;
  placed_at?: number;
  mine?: boolean;
  // Whale signal — backend may flag a big bet and its size relative to the room;
  // the frontend also derives these locally from the round's bets as a fallback.
  isWhale?: boolean;
  stakeMultiple?: number;
  stake_multiple?: number;
};

export type RoomDistribution = {
  roundNumber?: number;
  round_number?: number;
  roomCode?: string;
  room_code?: string;
  game?: string;
  phase?: string;
  status?: string;
  totalBets?: number;
  total_bets?: number;
  pools?: Record<string, { amount?: number; cap?: number; count?: number }>;
  summary?: Record<string, {
    betCount?: number;
    poolAmount?: number;
    poolCap?: number;
    potentialPayout?: number;
    maxPayout?: number;
    netExposure?: number;
  }>;
  breakdown?: Record<string, {
    bars?: Array<{ key?: string; count?: number; bettors?: number; amount?: number; potentialPayout?: number }>;
    grid?: Array<{ key?: string | number; count?: number; bettors?: number; amount?: number; potentialPayout?: number }>;
  }>;
  recentBets?: RoomDistributionBet[];
  recent_bets?: RoomDistributionBet[];
  bets?: RoomDistributionBet[];
  all_bets?: RoomDistributionBet[];
  betsComplete?: boolean;
  bets_complete?: boolean;
  visibility?: {
    individualBetsBlurred?: boolean;
  };
};

export function fetchRoomDistribution(
  roomCode: string,
  roundNumber: number,
  token: string,
  options: { includeBets?: boolean } = {},
) {
  const query = new URLSearchParams();
  if (options.includeBets) query.set('includeBets', 'true');
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return apiFetch<RoomDistribution>(`/api/rooms/${encodeURIComponent(roomCode)}/rounds/${encodeURIComponent(roundNumber)}/distribution${suffix}`, {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export type PresenceResponse = {
  ok: boolean;
  room_id: string;
  online: number;
  presence_ttl_sec: number;
};

export function heartbeatRoom(roomCode: string, token: string, visitorId: string) {
  return apiFetch<PresenceResponse>(`/api/rooms/${encodeURIComponent(roomCode)}/presence`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ visitor_id: visitorId }),
  });
}

export type BetRequest = {
  roomId: string;
  roundNumber: number;
  game: string;
  tab: string;
  pick: string;
  amount: number;
  // Copy-betting v0 attribution — sent so the backend can credit the followed
  // player's reward once that engine exists; ignored harmlessly until then.
  copiedFromUserId?: string | number;
  copiedFromBetId?: string | number;
};

export type ChipLine = {
  denom: number;
  qty: number;
};

export type StagedBetRequest = {
  game: string;
  tab: string;
  pick: string;
  amount: number;
  chips?: ChipLine[];
};

export type BatchBetRequest = {
  roomId: string;
  roundNumber: number;
  game: string;
  bets: StagedBetRequest[];
};

export type BetResponse = {
  bet?: BetItem;
  bets?: BetItem[];
  user?: PlayerUser;
  idempotent?: boolean;
};

export type BetResult = 'pending' | 'win' | 'lose' | 'cancelled' | 'won' | 'lost';

export type BetItem = {
  id?: number | string;
  round_number?: number;
  round?: number;
  cutoff_ts?: number;
  room_id?: string;
  room_code?: string;
  game?: string;
  tab?: string;
  pick?: string;
  amount?: number;
  currency?: string;
  status?: string;
  result?: BetResult;
  payout?: number;
  placed_at?: number;
  time?: number;
  /**
   * What the round produced. Absent (or `{}`) until the result is public, and
   * absent entirely on backends older than the 2026-08 bet-history rework — the
   * history renders without a draw chip in that case rather than guessing.
   */
  draw?: BetDraw;
};

export type BetDraw = {
  digits?: string;
  lastdigit?: number;
  sicbo?: number[];
  roulette?: number;
  /** Milhar 的开奖数，后端已按该档的数字窗口抽好。
   *  不让前端自己从 `digits` 里切 —— 窗口位置是 per-tier 配置（master §3.3），
   *  前端硬编码它就会在加档时悄悄错位。 */
  milhar?: string;
};

export type MyBetsResponse = {
  items?: BetItem[];
};

export function fetchMyBets(
  token: string,
  params: { roomCode?: string; roundNumber?: number; limit?: number } = {},
) {
  const query = new URLSearchParams();
  query.set('limit', String(params.limit || 100));
  if (params.roomCode) query.set('roomCode', params.roomCode);
  if (params.roundNumber) query.set('roundNumber', String(params.roundNumber));
  return apiFetch<MyBetsResponse>(`/api/bets/me?${query.toString()}`, {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export type RoundSummaryResponse = {
  settlementStatus?: 'pending' | 'settled' | string;
  roundNumber?: number;
  round_number?: number;
  bets?: BetItem[];
  draw?: {
    lastdigit?: number;
    sicbo?: number[];
    roulette?: number;
  };
  settlementAttempt?: {
    settledCount?: number;
    winCount?: number;
    payout?: number;
  };
};

export function fetchRoundSummary(token: string, roundNumber: number) {
  return apiFetch<RoundSummaryResponse>(`/api/bets/rounds/${encodeURIComponent(roundNumber)}/summary`, {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export function settleMyRound(token: string, roundNumber: number) {
  return apiFetch<RoundSummaryResponse>(`/api/bets/rounds/${encodeURIComponent(roundNumber)}/settle-my`, {
    method: 'POST',
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export type ChatMessage = {
  id?: number | string;
  room_id?: string;
  player_id?: string;
  nickname?: string;
  user?: {
    username?: string;
    name?: string;
    initial?: string;
    flag?: string;
    level?: number;
    vipLevel?: number;
    vip_level?: number;
    isAdmin?: boolean;
    is_admin?: boolean;
    // Forward-compat: the backend is expected to add these to the chat payload
    // (todo.md 2026-07-11) so other users' real avatars render. Until then they
    // are absent and the client falls back to the initial circle.
    avatar_url?: string;
    avatar_default_id?: number;
  };
  type?: 'text' | 'voice' | string;
  text?: string;
  ts?: number;
  voice_asset_id?: string;
  voice_url?: string;
  duration_sec?: number;
  mime_type?: string;
  mimeType?: string;
  file_size_bytes?: number;
  fileSizeBytes?: number;
};

export type ChatMessagesResponse = {
  room_id?: string;
  messages?: ChatMessage[];
};

export function fetchChatMessages(roomCode: string) {
  return apiFetch<ChatMessagesResponse>(`/api/chat/rooms/${encodeURIComponent(roomCode)}/messages?limit=50`, {
    cache: 'no-store',
  });
}

export type SendChatResponse = {
  message?: ChatMessage;
};

export function sendChatMessage(roomCode: string, token: string, text: string) {
  return apiFetch<SendChatResponse>(`/api/chat/rooms/${encodeURIComponent(roomCode)}/messages`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ text }),
  });
}

export async function sendVoiceMessage(roomCode: string, token: string, blob: Blob, durationSec: number) {
  const form = new FormData();
  const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : blob.type.includes('wav') ? 'wav' : 'webm';
  form.append('file', blob, `voice.${ext}`);
  form.append('duration_sec', String(Math.max(1, Math.min(30, Math.floor(durationSec)))));
  const response = await fetch(`/api/chat/rooms/${encodeURIComponent(roomCode)}/voice`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: form,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const code = payload && typeof payload === 'object' && 'code' in payload ? String(payload.code) : `HTTP_${response.status}`;
    throw new ApiError(code, response.status, payload);
  }
  return payload as SendChatResponse;
}

// Idempotency-Key must be STABLE across retries of one logical submission (a
// dropped response + re-tap must not double-charge) yet DISTINCT across separate
// ones. It must also be a format the backend accepts — the old colon-joined
// "react:round:room:…" string was rejected as INVALID_IDEMPOTENCY_KEY, so we mint
// a UUID (universally accepted) and cache it per content signature until the bet
// succeeds; retries of the same failed bet reuse the key, a new bet gets a fresh
// one.
const betIdemKeys = new Map<string, string>();

function newUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through to manual */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

function stableBetIdempotencyKey(signature: string): string {
  const existing = betIdemKeys.get(signature);
  if (existing) return existing;
  const key = newUuid();
  betIdemKeys.set(signature, key);
  return key;
}

function clientMode() {
  return window.matchMedia?.('(display-mode: standalone)').matches ? 'pwa-standalone' : 'web-browser';
}

export function placeBet(payload: BetRequest, token: string) {
  // Include the copy source so following two different players' identical
  // pick+amount are distinct logical bets (not collapsed by the idempotency
  // cache); a genuine retry of the *same* copy still reuses its key.
  const signature = `single:${payload.roundNumber}:${payload.roomId}:${payload.game}:${payload.tab}:${payload.pick}:${payload.amount}:${payload.copiedFromBetId ?? payload.copiedFromUserId ?? 'direct'}`;
  const idempotencyKey = stableBetIdempotencyKey(signature);

  return apiFetch<BetResponse>('/api/bets', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
      'Idempotency-Key': idempotencyKey,
    },
    body: jsonBody({
      ...payload,
      idempotencyKey,
      clientMode: clientMode(),
    }),
  }).then((result) => {
    betIdemKeys.delete(signature); // success — the next bet earns a fresh key
    return result;
  });
}

export function placeBetsBatch(payload: BatchBetRequest, token: string) {
  const signature = `batch:${payload.roundNumber}:${payload.roomId}:${payload.game}:${JSON.stringify(payload.bets)}`;
  const idempotencyKey = stableBetIdempotencyKey(signature);

  return apiFetch<BetResponse>('/api/bets', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
      'Idempotency-Key': idempotencyKey,
    },
    body: jsonBody({
      ...payload,
      idempotencyKey,
      clientMode: clientMode(),
    }),
  }).then((result) => {
    betIdemKeys.delete(signature);
    return result;
  });
}
